import React from "react";
import { Link } from "react-router-dom";
import { Flame, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { PILLARS } from "../data/pillars";

export default function Footer() {
  const { t, pick } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="relative mt-24 border-t border-tara-gold/25 bg-gradient-to-b from-[#F5FAF9] to-[#EAF1F0]" data-testid="footer">
      <div className="pattern-sacred absolute inset-0 opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-tara-gold to-tara-vermilion shadow-soft">
                <Flame className="h-5 w-5 text-white" />
              </span>
              <span className="font-serif text-lg font-bold gold-text">TarapithTantrik</span>
            </div>
            <p className="text-sm text-tara-body leading-relaxed">{t("footer_tagline")}</p>
            <p className="mt-5 font-display text-xl text-tara-ink">{SITE.practitioner}</p>
            <p className="text-xs uppercase tracking-[0.18em] text-tara-muted mt-1">Tantrik Sadhak &amp; Vedic Astrologer</p>
          </div>

          <div>
            <h4 className="font-serif text-tara-goldDeep text-xs tracking-[0.24em] uppercase mb-5">{t("footer_explore")}</h4>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/astrology", label: t("nav_astrology"), id: "astrology" },
                { to: "/astrology/kundali", label: "Free Kundali", id: "kundali" },
                { to: "/knowledge", label: t("nav_knowledge"), id: "knowledge" },
                { to: "/life-concerns", label: t("nav_concerns"), id: "concerns" },
                { to: "/festivals", label: t("nav_festivals"), id: "festivals" },
                { to: "/visit", label: t("nav_visit"), id: "visit" },
                { to: "/about", label: t("nav_about"), id: "about" },
                { to: "/contact", label: t("nav_contact"), id: "contact" },
              ].map((l) => (
                <li key={l.id}>
                  <Link to={l.to} className="text-tara-body hover:text-tara-sindoor transition-colors" data-testid={`footer-${l.id}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-tara-goldDeep text-xs tracking-[0.24em] uppercase mb-5">{t("footer_knowledge")}</h4>
            <ul className="space-y-3 text-sm">
              {PILLARS.slice(0, 6).map((p) => (
                <li key={p.slug}>
                  <Link to={`/knowledge/${p.slug}`} className="text-tara-body hover:text-tara-sindoor transition-colors">
                    {pick(p, "title")}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-tara-goldDeep text-xs tracking-[0.24em] uppercase mb-5">{t("footer_connect")}</h4>
            <a href={`tel:+${SITE.whatsapp}`} className="flex items-center gap-2 text-sm text-tara-ink font-medium hover:text-tara-sindoor transition-colors mb-3" data-testid="footer-phone">
              <Phone className="h-4 w-4 text-tara-gold" /> {SITE.phoneDisplay}
            </a>
            <a
              href={`https://wa.me/${SITE.whatsapp}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700 transition-colors mb-6"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <ul className="space-y-3 text-sm">
              <li><Link to="/privacy" className="text-tara-muted hover:text-tara-sindoor transition-colors">{t("privacy")}</Link></li>
              <li><Link to="/terms" className="text-tara-muted hover:text-tara-sindoor transition-colors">{t("terms")}</Link></li>
              <li><Link to="/disclaimer" className="text-tara-muted hover:text-tara-sindoor transition-colors">{t("disclaimer")}</Link></li>
              <li><Link to="/editorial" className="text-tara-muted hover:text-tara-sindoor transition-colors">Editorial Policy</Link></li>
            </ul>
          </div>
        </div>

        <div className="divider-om my-10" />
        <p className="text-xs text-tara-muted leading-relaxed mb-3 max-w-3xl">{t("disclaimer_short")}</p>
        <p className="text-xs text-tara-muted/80">© {year} {SITE.name}. {t("rights")}</p>
      </div>
    </footer>
  );
}
